import { useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import Header from "@/pages/home/components/Header";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useCurrentLang } from "@/hooks/useLanguage";
import { getMemberPortalUrl } from "@/constants/links";

/* ──────────────────────── Hero Section ──────────────────────── */

function FrHeroSection() {
  const handleScrollToPlan = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.getElementById("summary-plan");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 56;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, []);

  return (
    <section id="home" className="relative w-full overflow-hidden" style={{ minHeight: "520px" }}>
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=elegant%20concert%20hall%20interior%20with%20warm%20golden%20stage%20lighting%2C%20rows%20of%20empty%20seats%2C%20grand%20architecture%2C%20deep%20navy%20and%20gold%20tones%2C%20sophisticated%20atmosphere%2C%20professional%20photography%2C%20wide%20angle%2C%20no%20people%2C%20classical%20music%20venue%2C%20ornate%20ceiling%20details&width=1400&height=600&seq=mpf-hero-bg-01&orientation=landscape')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0d1f4c]/90 via-[#0d1f4c]/75 to-[#0d1f4c]/40" />
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 py-20 md:py-28 w-full">
        <div className="max-w-[620px] motion-safe:animate-[heroFadeIn_0.7s_ease-out_forwards]">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-0.5 bg-[#1a8fc1]"></div>
            <span className="text-[#7ec8e3] text-sm font-semibold tracking-widest uppercase">
              Caisse de retraite des musiciens du Canada
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
            Aider les musiciens canadiens à bâtir leur{" "}
            <span className="text-[#7ec8e3]">sécurité de retraite</span>
          </h1>
          <p className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-[520px]">
            La Caisse de retraite des musiciens du Canada administre des prestations de retraite pour les membres et bénéficiaires admissibles partout au Canada.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="/fr#summary-plan"
              onClick={handleScrollToPlan}
              className="flex items-center gap-2 px-6 py-3 bg-[#1a8fc1] hover:bg-[#1578a3] text-white font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-file-text-line"></i>
              En savoir plus sur le régime
            </a>
            <Link
              to="/fr/resources"
              className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/40 text-white font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm backdrop-blur-sm"
            >
              <i className="ri-folder-open-line"></i>
              Trouver des ressources
            </Link>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#f4f8fc] to-transparent" />
    </section>
  );
}

/* ──────────────────────── Quick Links ──────────────────────── */

function FrQuickLinks() {
  const handleMemberScroll = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.getElementById("member");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 56;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, []);

  interface QuickLinkCard {
    href: string;
    icon: string;
    label: string;
    description: string;
    actionText: string;
  }

  const cards: QuickLinkCard[] = [
    {
      href: "/fr#member",
      icon: "ri-shield-user-line",
      label: "Portail membre",
      description: "Apprenez comment accéder à votre portail membre sécurisé et aux renseignements sur votre compte de retraite.",
      actionText: "Accès au portail",
    },
    {
      href: "/fr/resources",
      icon: "ri-file-list-3-line",
      label: "Ressources et formulaires",
      description: "Trouvez les rapports annuels, bulletins d'information, formulaires, avis et documents de référence.",
      actionText: "Voir les ressources",
    },
    {
      href: "/fr/news",
      icon: "ri-newspaper-line",
      label: "Actualités et mises à jour",
      description: "Lisez les dernières annonces, avis et mises à jour de la Caisse.",
      actionText: "Voir les actualités",
    },
    {
      href: "/fr/faq",
      icon: "ri-question-answer-line",
      label: "FAQ",
      description: "Trouvez des réponses aux questions courantes sur les cotisations, les prestations de retraite et la Caisse.",
      actionText: "Voir la FAQ",
    },
  ];

  function CardContent({ card }: { card: QuickLinkCard }) {
    return (
      <>
        <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#e8f4fb] mb-5 transition-colors duration-200 group-hover:bg-[#d0eaf7]">
          <i className={`${card.icon} text-[22px] text-[#1a8fc1]`}></i>
        </div>
        <h3 className="text-base font-semibold text-[#0d1f4c] mb-2 group-hover:text-[#1a8fc1] transition-colors duration-200">
          {card.label}
        </h3>
        <p className="text-[13px] text-gray-500 leading-relaxed mb-5">
          {card.description}
        </p>
        <div className="flex items-center gap-1.5 text-[#1a8fc1] text-sm font-medium mt-auto">
          <span className="whitespace-nowrap">{card.actionText}</span>
          <i className="ri-arrow-right-line text-sm transition-transform duration-200 group-hover:translate-x-1"></i>
        </div>
      </>
    );
  }

  return (
    <section className="w-full bg-[#f4f8fc] py-14">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((card) => {
            const isHashLink = card.href.startsWith("/fr#");
            if (isHashLink) {
              return (
                <a
                  key={card.label}
                  href={card.href}
                  onClick={handleMemberScroll}
                  className="group flex flex-col bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:border-[#1a8fc1]/50 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                >
                  <CardContent card={card} />
                </a>
              );
            }
            return (
              <Link
                key={card.label}
                to={card.href}
                className="group flex flex-col bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:border-[#1a8fc1]/50 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
              >
                <CardContent card={card} />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────── Mission Section ──────────────────────── */

function FrMissionSection() {
  const fiduciaires = [
    "William Stafford",
    "Allistair Elliott",
    "David Owen",
    "Grace Huang",
  ];

  return (
    <section id="about" className="w-full bg-white py-16 scroll-mt-14">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-start gap-12">
          <div className="w-full lg:w-[420px] flex-shrink-0">
            <div className="rounded-lg overflow-hidden" style={{ height: "420px" }}>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20orchestra%20musicians%20performing%20on%20stage%2C%20warm%20stage%20lighting%2C%20formal%20black%20attire%2C%20focused%20and%20dedicated%20performers%2C%20elegant%20concert%20hall%20setting%2C%20deep%20navy%20and%20warm%20gold%20tones%2C%20high%20quality%20photography%2C%20wide%20shot%20showing%20multiple%20musicians%20with%20instruments&width=840&height=840&seq=mpf-about-img-01&orientation=landscape"
                alt="Caisse de retraite des musiciens du Canada"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-0.5 bg-[#1a8fc1]"></div>
              <span className="text-[#1a8fc1] text-xs font-semibold tracking-widest uppercase">
                À propos
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0d1f4c] mb-5 leading-snug">
              À propos de la Caisse
            </h2>
            <p className="text-base text-gray-600 leading-relaxed mb-4">
              La Caisse de retraite des musiciens du Canada administre des prestations de retraite
              pour les membres et bénéficiaires admissibles partout au Canada. La Caisse est régie
              par un conseil de fiduciaires et soutenue par le Bureau de la Caisse.
            </p>
            <div className="bg-[#f3f6fa] border-l-[4px] border-[#1a8fc1] rounded-r-lg px-5 py-4 mb-6">
              <p className="text-sm text-gray-700 leading-relaxed italic">
                Notre mission est d'administrer le régime de manière prudente afin d'assurer
                sa viabilité à long terme dans le but d'offrir les meilleures prestations
                de retraite possible aux membres et aux bénéficiaires de la Caisse.
              </p>
            </div>
            <div className="bg-[#f8fafc] border border-gray-200 border-t-[3px] border-t-[#1a8fc1] rounded-lg p-6">
              <h3 className="text-base font-semibold text-[#0d1f4c] mb-4 flex items-center gap-2">
                <i className="ri-government-line text-[#1a8fc1]"></i>
                Gouvernance de la Caisse
              </h3>
              <div className="space-y-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-medium text-gray-500 tracking-wider w-[185px] flex-shrink-0 whitespace-nowrap">
                    Directeur général
                  </span>
                  <span className="text-sm font-semibold text-[#0d1f4c]">
                    Paul Versteeg-Lytwyn
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-medium text-gray-500 tracking-wider w-[185px] flex-shrink-0 whitespace-nowrap">
                    Présidente
                  </span>
                  <span className="text-sm font-semibold text-[#0d1f4c]">
                    Ellen Versteeg-Lytwyn
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-medium text-gray-500 tracking-wider w-[185px] flex-shrink-0 whitespace-nowrap">
                    Vice-président
                  </span>
                  <span className="text-sm font-semibold text-[#0d1f4c]">
                    Bernard LeBlanc
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-medium text-gray-500 tracking-wider w-[185px] flex-shrink-0 pt-0.5 whitespace-nowrap">
                      Fiduciaires
                    </span>
                    <div className="text-sm text-[#0d1f4c] space-y-1">
                      {fiduciaires.map((nom) => (
                        <div key={nom} className="font-medium">
                          {nom}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────── Summary Plan ──────────────────────── */

function FrSummaryPlanSection() {
  const planCards = [
    {
      icon: "ri-group-line",
      titre: "Régime de retraite interentreprises",
      corps: "La Caisse de retraite des musiciens du Canada est un régime de retraite interentreprises destiné aux musiciens et autres membres admissibles. Il est financé par les cotisations des employeurs versées en vertu des ententes applicables et administré par un conseil de fiduciaires.",
    },
    {
      icon: "ri-coins-line",
      titre: "Cotisations des employeurs",
      corps: "Les cotisations des employés ne sont ni requises ni permises. Les employeurs cotisent à la Caisse au nom des membres admissibles, et ces cotisations sont détenues en fiducie pour verser les prestations et payer les dépenses administratives de la Caisse.",
    },
    {
      icon: "ri-calendar-check-line",
      titre: "Prestations de retraite",
      corps: "Le régime prévoit des prestations de retraite normales, anticipées, différées, de retraite spéciale, d'invalidité et de survivant. Les prestations de retraite ne sont pas automatiques; les membres doivent présenter une demande et soumettre les formulaires requis.",
    },
  ];

  return (
    <section id="summary-plan" className="w-full bg-[#f4f8fc] py-16 scroll-mt-14">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-0.5 bg-[#1a8fc1]"></div>
          <span className="text-[#1a8fc1] text-xs font-semibold tracking-widest uppercase">
            Guide du régime
          </span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-[#0d1f4c] mb-4">
          Résumé du régime
        </h2>
        <p className="text-sm text-gray-500 leading-relaxed max-w-[780px]">
          Ce guide en langage clair décrit les dispositions clés de la Caisse de retraite des musiciens du Canada,
          y compris l'admissibilité, les cotisations, le calcul des prestations, les options de retraite et les
          règles de distribution. Voici un aperçu du fonctionnement du régime.
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {planCards.map((carte) => (
            <div
              key={carte.titre}
              className="bg-white rounded-lg border border-gray-200 border-t-[3px] border-t-[#1a8fc1] shadow-sm p-7"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#e8f4fb] mb-5">
                <i className={`${carte.icon} text-xl text-[#1a8fc1]`}></i>
              </div>
              <h3 className="text-base font-semibold text-[#0d1f4c] mb-3 leading-snug">
                {carte.titre}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                {carte.corps}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-8 bg-[#f8fafc] border border-gray-200 border-l-[4px] border-l-[#1a8fc1] rounded-r-lg rounded-l shadow-sm p-6 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg bg-[#f4f8fc]">
            <i className="ri-file-pdf-2-line text-2xl text-[#1a8fc1]"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-[#0d1f4c] mb-1 whitespace-nowrap">
              Résumé du régime 2019
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              Guide en langage clair du régime et de ses principales dispositions.
            </p>
          </div>
          <a
            href="/wp-content/uploads/2019/01/MPFCanada_PensionPlan2019_French-FINAL-s.pdf"
            target="_blank"
            rel="nofollow"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#1a8fc1] text-white text-sm font-medium rounded-lg hover:bg-[#157ba6] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            Voir
            <i className="ri-external-link-line text-sm"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────── Plan Rules ──────────────────────── */

function FrPlanRulesSection() {
  const documents = [
    { titre: "Règles et règlements du régime", url: "/wp-content/uploads/2018/09/MPF-Restated-Plan-Text-Jan-1-2018_FRENCH.pdf" },
    { titre: "Modification n° 1", url: "/wp-content/uploads/2021/02/Amendement-No-1-Decembre-2020.pdf" },
    { titre: "Modification n° 2", url: "/wp-content/uploads/2021/02/Amendement-No-2-Decembre-2020.pdf" },
    { titre: "Modification n° 3", url: "/wp-content/uploads/2021/09/PlanAmendmentNo.3FR.pdf" },
    { titre: "Modification n° 4", url: "/wp-content/uploads/2022/03/Amendment-4-French.pdf" },
    { titre: "Modification n° 5", url: "/wp-content/uploads/2022/11/Amendment-No-5-FR.pdf" },
  ];

  return (
    <section id="plan-rules" className="w-full bg-white py-16 scroll-mt-14">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-0.5 bg-[#1a8fc1]"></div>
          <span className="text-[#1a8fc1] text-xs font-semibold tracking-widest uppercase">
            Documents constitutifs
          </span>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-100">
            <i className="ri-book-2-line text-lg text-[#0d1f4c]"></i>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#0d1f4c]">
            Règles du régime
          </h2>
        </div>
        <p className="text-sm text-gray-500 leading-relaxed max-w-[720px]">
          Les règles et règlements du régime sont les documents constitutifs officiels de la Caisse de retraite
          des musiciens du Canada. Ils énoncent les conditions juridiques, les critères d'admissibilité,
          les dispositions relatives aux prestations et les procédures administratives qui régissent
          le fonctionnement de la Caisse.
        </p>
        <div className="mt-6">
          <div className="bg-[#f8fafc] rounded-lg border border-gray-200 border-t-[3px] border-t-[#1a8fc1] shadow-sm overflow-hidden">
            {documents.map((doc, index) => (
              <div
                key={index}
                className="flex items-center gap-3 py-2.5 px-5 border-b border-gray-100 last:border-b-0 hover:bg-white/60 transition-colors duration-150"
              >
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md bg-[#e8f4fb]">
                  <i className="ri-file-pdf-2-line text-sm text-[#1a8fc1]"></i>
                </div>
                <h3 className="flex-1 min-w-0 text-sm font-medium text-[#0d1f4c] truncate">
                  {doc.titre}
                </h3>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="nofollow"
                  className="inline-flex items-center gap-1 px-3.5 py-1.5 text-xs font-medium text-[#1a8fc1] border border-[#1a8fc1] rounded-md hover:bg-[#e8f4fb] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                >
                  Voir
                  <i className="ri-external-link-line text-xs"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────── Member Portal ──────────────────────── */

function FrMemberPortalSection() {
  const lang = useCurrentLang();
  const isFrench = lang === "fr";

  const handleContactClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const el = document.getElementById("contact");
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 56;
        window.scrollTo({ top, behavior: "smooth" });
      }
    },
    [],
  );

  return (
    <section id="member" className="w-full bg-[#f4f8fc] py-16 scroll-mt-14">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-0.5 bg-[#1a8fc1]"></div>
          <span className="text-[#1a8fc1] text-xs font-semibold tracking-widest uppercase">
            Accès membre
          </span>
        </div>
        <div className="flex flex-col lg:flex-row gap-10">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-100">
                <i className="ri-shield-user-line text-lg text-[#0d1f4c]"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0d1f4c]">
                Portail membre
              </h2>
            </div>
            <div className="max-w-[560px] space-y-5">
              <p className="text-sm text-gray-500 leading-relaxed">
                Les membres peuvent utiliser le portail membre sécurisé pour accéder aux renseignements
                disponibles sur leur compte de retraite, y compris les relevés et autres ressources pour les membres.
              </p>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2.5 text-sm text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#1a8fc1] text-sm"></i>
                  </div>
                  <span>Consulter les renseignements disponibles sur votre compte de retraite</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#1a8fc1] text-sm"></i>
                  </div>
                  <span>Accéder aux relevés et aux ressources pour les membres</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#1a8fc1] text-sm"></i>
                  </div>
                  <span>Utiliser l'accès libre-service sécurisé en ligne</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="lg:w-[380px] flex-shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm border-t-[3px] border-t-[#1a8fc1] p-6">
              <h3 className="text-sm font-semibold text-[#0d1f4c] mb-4">
                Accès au portail
              </h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2.5 text-xs text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-[#1a8fc1] text-xs"></i>
                  </div>
                  <span>Nom d'utilisateur : adresse courriel au dossier de la Caisse de retraite des musiciens du Canada</span>
                </li>
                <li className="flex items-start gap-2.5 text-xs text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-lock-password-line text-[#1a8fc1] text-xs"></i>
                  </div>
                  <span>Première connexion ou mot de passe oublié : utilisez Mot de passe oublié sur le portail</span>
                </li>
                <li className="flex items-start gap-2.5 text-xs text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-question-line text-[#1a8fc1] text-xs"></i>
                  </div>
                  <span>Besoin d'aide? <a href="/fr#contact" className="text-[#1a8fc1] hover:underline transition-colors" onClick={handleContactClick}>Communiquez avec le Bureau de la Caisse</a></span>
                </li>
              </ul>
              <a
                href={getMemberPortalUrl(isFrench ? 'fr' : 'en')}
                target="_blank"
                rel="nofollow"
                className="inline-flex items-center gap-2 w-full justify-center px-6 py-3 bg-[#1a8fc1] hover:bg-[#157ba6] text-white font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Accéder au compte Web
                <i className="ri-arrow-right-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ──────────────────────── Page ──────────────────────── */

export default function FrHome() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    if (id === "home") return;
    const scrollToSection = () => {
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 56;
        window.scrollTo({ top, behavior: "smooth" });
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToSection);
    });
  }, [location.hash]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Navbar />
      <main className="flex-1">
        <FrHeroSection />
        <FrQuickLinks />
        <FrMissionSection />
        <FrSummaryPlanSection />
        <FrPlanRulesSection />
        <FrMemberPortalSection />
      </main>
      <Footer />
    </div>
  );
}